/**
 * useJobs 훅
 *
 * 책임:
 * - Job 목록 페칭 (필터 + 페이지네이션)
 * - 로딩/에러/데이터 상태 관리
 * - 필터 + 페이지 상태 관리
 * - 필터 변경 시 페이지 1로 자동 리셋
 * - 재시도 기능
 *
 * JobsContainer에서 사용
 */

import { useEffect, useState, useCallback } from 'react';
import * as endpoints from '../api/endpoints';
// 타입은 api/types에서 import (endpoints.ts는 이 타입들을 재수출하지 않음)
import type { JobListResponse, JobFilters } from '../api/types';

export interface UseJobsReturn {
  data: JobListResponse | null;
  loading: boolean;
  error: string | null;
  filters: JobFilters;
  setFilters: (filters: JobFilters) => void;
  page: number;
  setPage: (page: number) => void;
  retry: () => void;
}

export function useJobs(
  token: string | null,
  initialFilters?: JobFilters,
  pageSize = 50
): UseJobsReturn {
  const [data, setData] = useState<JobListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<JobFilters>(initialFilters ?? {});
  const [page, setPage] = useState(0);

  // 데이터 페칭: 현재 필터 + 페이지에 해당하는 Job 목록 조회
  const fetch = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const offset = page * pageSize; // page 0 → offset 0, page 1 → offset pageSize
      const result = await endpoints.fetchJobs(filters, pageSize, offset);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [token, filters, page, pageSize]);

  // 필터 변경 또는 페이지 크기 변경(뷰 전환) 시 페이지를 1로 리셋
  useEffect(() => {
    setPage(0);
  }, [filters, pageSize]);

  // 의존성 (filter, page) 변경 시 fetch
  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, filters, setFilters, page, setPage, retry: fetch };
}
